import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Lightning, Gauge, Rocket } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

const WebsiteSpeedHero = () => {
  const { t } = useTranslation();
  const heroRef = useRef(null);
  const blobRef = useRef(null);
  const speedRef = useRef(null);
  const iconsRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate the blob shape
      gsap.fromTo(blobRef.current, 
        { 
          scale: 0.8, 
          rotation: -5,
          borderRadius: '60% 40% 30% 70% / 60% 30% 70% 40%'
        },
        { 
          scale: 1, 
          rotation: 0,
          borderRadius: '30% 60% 70% 40% / 50% 60% 30% 60%',
          duration: 2,
          ease: "power2.out"
        }
      );

      // Animate the speed visualization
      gsap.fromTo(speedRef.current,
        { 
          y: 50, 
          opacity: 0,
          rotationY: -15
        },
        { 
          y: 0, 
          opacity: 1,
          rotationY: 0,
          duration: 1.5,
          delay: 0.5,
          ease: "back.out(1.7)"
        }
      );

      // Animate floating icons
      gsap.to(iconsRef.current.children, {
        y: -20,
        duration: 2,
        ease: "power2.inOut",
        stagger: 0.2,
        yoyo: true,
        repeat: -1
      });

      // Continuous blob morphing
      gsap.to(blobRef.current, {
        borderRadius: '70% 30% 50% 50% / 30% 50% 70% 70%',
        duration: 4,
        ease: "power2.inOut",
        yoyo: true,
        repeat: -1
      });

    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={heroRef} className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 w-24 h-24 bg-blue-500/20 rounded-full blur-2xl animate-pulse delay-500"></div>
      </div>

      {/* Main Hero Content */}
      <div className="container mx-auto px-4 pt-20 pb-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center min-h-[80vh]">
          {/* Left Content */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <h1 className="text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              Website snelheid{' '}
              <span className="text-blue-400">die telt</span>
            </h1>
            <p className="text-blue-400 text-xl font-medium mb-4">
              Maak je website razendsnel en verbeter je conversies
            </p>
            <p className="text-gray-300 text-lg mb-8 leading-relaxed max-w-2xl">
              Van performance analyse tot code optimalisatie. We maken je website 
              supersnel zodat bezoekers blijven en je Google ranking verbetert.
            </p>
            
            {/* CTA Buttons */}
            <div className="space-y-4">
              <div>
                <Link to="/start-your-project" className="inline-flex items-center bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg font-bold text-lg group transition-all duration-300 transform hover:scale-105">
                  GRATIS SPEED TEST
                  <ArrowRight className="ml-3 group-hover:translate-x-1 transition-transform" weight="thin" />
                </Link>
              </div>
              <div>
                <Link to="/contact" className="inline-flex items-center text-gray-300 hover:text-blue-400 transition-colors cursor-pointer group">
                  <span className="mr-2">Liever eerst sparren?</span>
                  <ArrowRight className="group-hover:translate-x-1 transition-transform" weight="thin" />
                </Link>
              </div>
            </div>

            {/* Feature Icons */}
            <div ref={iconsRef} className="flex items-center space-x-8 mt-12">
              <div className="flex flex-col items-center text-center">
                <div className="bg-white/10 backdrop-blur-sm p-3 rounded-full mb-2">
                  <Lightning className="text-blue-400" size={24} weight="thin" />
                </div>
                <span className="text-white/80 text-sm">Snelle Laadtijd</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="bg-white/10 backdrop-blur-sm p-3 rounded-full mb-2">
                  <Gauge className="text-blue-400" size={24} weight="thin" />
                </div>
                <span className="text-white/80 text-sm">Performance</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="bg-white/10 backdrop-blur-sm p-3 rounded-full mb-2">
                  <Rocket className="text-blue-400" size={24} weight="thin" />
                </div>
                <span className="text-white/80 text-sm">SEO Boost</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="bg-white/10 backdrop-blur-sm p-3 rounded-full mb-2">
                  <Lightning className="text-blue-400" size={24} weight="thin" />
                </div>
                <span className="text-white/80 text-sm">Conversie</span>
              </div>
            </div>
          </motion.div>

          {/* Right Visual Section */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="relative flex justify-center items-center hidden lg:flex"
          >
            {/* Animated Blob Container */}
            <div 
              ref={blobRef}
              className="relative w-[500px] h-[600px] bg-gradient-to-br from-blue-500/20 via-blue-500/20 to-blue-500/20 backdrop-blur-sm rounded-[30%_60%_70%_40%_/_50%_60%_30%_60%] flex items-center justify-center"
              style={{
                boxShadow: '0 0 100px rgba(59, 130, 246, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              {/* Speed Visualization */}
              <div 
                ref={speedRef}
                className="relative w-80 h-80 flex items-center justify-center"
              >
                {/* Speedometer */}
                <div className="relative w-64 h-64 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 flex items-center justify-center">
                  {/* Speedometer Background */}
                  <div className="w-56 h-56 rounded-full border-4 border-white/30 relative">
                    {/* Speed Arc - Animated rotation */}
                    <div className="absolute inset-2 rounded-full border-8 border-transparent border-t-blue-400 border-r-blue-400 border-b-blue-400 border-l-blue-400 animate-spin"></div>
                    
                    {/* Inner pulse circle */}
                    <div className="absolute inset-6 rounded-full bg-blue-500/20 animate-pulse"></div>
                    
                    {/* Center Display */}
                    <div className="absolute inset-0 flex items-center justify-center flex-col">
                      <div className="text-4xl font-bold text-white mb-2">98</div>
                      <div className="text-blue-300 text-sm">PageSpeed</div>
                      <div className="text-green-400 text-lg font-semibold mt-1">Fast!</div>
                    </div>
                    
                    {/* Speed Indicators around the circle */}
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-2 h-8 bg-green-400 rounded-full"></div>
                                    <div className="absolute top-6 -right-4 w-2 h-8 bg-blue-400 rounded-full transform rotate-45"></div>
                <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-2 h-8 bg-blue-400 rounded-full"></div>
                    <div className="absolute top-6 -left-4 w-2 h-8 bg-red-400 rounded-full transform -rotate-45"></div>
                  </div>
                  
                  {/* Moving Speed Lines - animated */}
                  <div className="absolute inset-0">
                    <div className="absolute top-4 left-8 w-12 h-1 bg-blue-400 rounded-full opacity-70 animate-pulse"></div>
                    <div className="absolute top-12 right-6 w-8 h-1 bg-blue-400 rounded-full opacity-50 animate-pulse delay-100"></div>
                    <div className="absolute bottom-16 left-6 w-10 h-1 bg-blue-400 rounded-full opacity-60 animate-pulse delay-200"></div>
                    <div className="absolute bottom-8 right-8 w-14 h-1 bg-blue-400 rounded-full opacity-80 animate-pulse delay-300"></div>
                  </div>
                </div>
                
                {/* Performance Metrics */}
                <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 flex space-x-6">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 text-center border border-white/20">
                    <div className="text-green-400 font-bold text-lg">0.5s</div>
                    <div className="text-white/70 text-xs">Load Time</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 text-center border border-white/20">
                    <div className="text-blue-400 font-bold text-lg">95%</div>
                    <div className="text-white/70 text-xs">Score</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2 text-center border border-white/20">
                    <div className="text-blue-400 font-bold text-lg">A+</div>
                    <div className="text-white/70 text-xs">Grade</div>
                  </div>
                </div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-blue-500/30 rounded-full blur-sm"></div>
              <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-blue-500/30 rounded-full blur-sm"></div>
              <div className="absolute top-1/2 -right-8 w-12 h-12 bg-blue-500/30 rounded-full blur-sm"></div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default WebsiteSpeedHero;
